import { useState, useEffect } from 'react';
import { 
  User, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  AuthError
} from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { auth } from '../utils/firebase';
import { clearUserExperienceStorage } from '../utils/userExperienceStorage';

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

interface AuthActions {
  signUp: (email: string, password: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  clearError: () => void;
}

export const useAuth = (): AuthState & AuthActions => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signUp = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const authError = err as AuthError;
      setError(getAuthErrorMessage(authError.code, t));
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      const authError = err as AuthError;
      setError(getAuthErrorMessage(authError.code, t));
    } finally {
      setLoading(false);
    }
  };

  const signOutUser = async () => {
    try {
      setError(null);
      await signOut(auth);
      // Drop onboarding / servers / account configs so the next user starts fresh.
      // Reload so in-memory hooks (journey, servers, etc.) cannot rehydrate stale state.
      clearUserExperienceStorage();
      window.location.assign('/');
    } catch (err) {
      const authError = err as AuthError;
      setError(getAuthErrorMessage(authError.code, t));
    }
  };

  const resetPassword = async (email: string) => {
    try {
      setError(null);
      setLoading(true);
      await sendPasswordResetEmail(auth, email);
    } catch (err) {
      const authError = err as AuthError;
      setError(getAuthErrorMessage(authError.code, t));
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    setError(null);
  };

  return {
    user,
    loading,
    error,
    signUp,
    signIn,
    signOut: signOutUser,
    resetPassword,
    clearError
  };
};

// Helper function to get user-friendly error messages
const getAuthErrorMessage = (errorCode: string, t: (key: string, options?: any) => string): string => {
  switch (errorCode) {
    case 'auth/email-already-in-use':
      return t('errors.emailAlreadyInUse', { ns: 'auth' });
    case 'auth/invalid-email':
      return t('errors.invalidEmail', { ns: 'auth' });
    case 'auth/operation-not-allowed':
      return t('errors.operationNotAllowed', { ns: 'auth' });
    case 'auth/weak-password':
      return t('errors.weakPassword', { ns: 'auth' });
    case 'auth/user-disabled':
      return t('errors.userDisabled', { ns: 'auth' });
    case 'auth/user-not-found':
      return t('errors.userNotFound', { ns: 'auth' });
    case 'auth/wrong-password':
      return t('errors.wrongPassword', { ns: 'auth' });
    case 'auth/too-many-requests':
      return t('errors.tooManyRequests', { ns: 'auth' });
    case 'auth/network-request-failed':
      return t('errors.networkRequestFailed', { ns: 'auth' });
    default:
      return t('errors.default', { ns: 'auth' });
  }
}; 